import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Banner,
  Button,
  Card,
  Layout,
  List,
  Modal,
  ChoiceList,
  Select,
  Stack,
  TextField,
  TextContainer,
  Toast,
  Tooltip,
} from "@shopify/polaris";
import { connect } from "react-redux";
import bulkActionApi from "../../services/bulkActionApi";
import productsApi from "../../services/productsApi";
import { refreshSettings } from "../../redux/actions/settingsActions";
import { isCatalogPriceUpdateRunning } from "../common/CatalogPriceUpdateBanner";

const MAX_CSV_BYTES = 10 * 1024 * 1024; // 10MB
const UPLOAD_SLOW_FALLBACK_MS = 10000;
const CSV_UPLOAD_MAX_QUEUE_COUNT = 15;

const UPLOAD_TOOLTIP_COMING_SOON = "Coming Soon";
const UPLOAD_TOOLTIP_IN_PROGRESS =
  "CSV upload is currently in progress. You cannot upload at this time.";
const UPLOAD_TOOLTIP_CATALOG_UPDATE_IN_PROGRESS =
  "Market price update is in progress. Please upload after some time.";
const UPLOAD_TOOLTIP_HIGH_TRAFFIC =
  "We have high traffic right now. Please check back after some time.";
const DOWNLOAD_TOOLTIP_EXPORT_IN_PROGRESS =
  "A CSV export is in progress. Please wait until your file is ready to download from the banner.";

const UploadCsv = ({
  embedded = false,
  sectionTitle,
  sectionDescription,
  downloadCSV,
  bulkDownloadApiCalled = false,
  shopName = '',
  isCsvUploadJobRunning = false,
  isCsvDownloadRunning = false,
  csvUploadJobTotalCount = 0,
  catalogPriceUpdateRunningMarkets = [],
  refreshSettings,
}) => {
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [toastActive, setToastActive] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastError, setToastError] = useState(false);
  const [validationModalOpen, setValidationModalOpen] = useState(false);
  const [validationModalData, setValidationModalData] = useState(null);
  const [tooManyErrorsModalOpen, setTooManyErrorsModalOpen] = useState(false);
  const [tooManyErrorsModalData, setTooManyErrorsModalData] = useState(null);
  const [uploadSuccessModalOpen, setUploadSuccessModalOpen] = useState(false);
  const [uploadSuccessMessage, setUploadSuccessMessage] = useState("");
  const [uploadEmailModalOpen, setUploadEmailModalOpen] = useState(false);
  const [csvUploadEnabled, setCsvUploadEnabled] = useState(false);
  const [csvUploadFeatureLoading, setCsvUploadFeatureLoading] = useState(false);
  const [downloadOptionsModalOpen, setDownloadOptionsModalOpen] = useState(false);
  const [downloadScope, setDownloadScope] = useState("all");
  const [availableCollections, setAvailableCollections] = useState([]);
  const [selectedCollectionIds, setSelectedCollectionIds] = useState([]);
  const [collectionSearch, setCollectionSearch] = useState("");
  const [collectionsLoading, setCollectionsLoading] = useState(false);
  const [collectionsError, setCollectionsError] = useState("");
  const fileInputRef = useRef(null);
  const uploadSlowHandledRef = useRef(false);

  // useEffect(() => {
  //   if (!shopName) {
  //     setCsvUploadEnabled(false);
  //     setCsvUploadFeatureLoading(false);
  //     return undefined;
  //   }
  //   let cancelled = false;

  //   bulkActionApi
  //     .getCsvUploadFeature(shopName)
  //     .then((res) => {
  //       if (cancelled) return;
  //       const data = res?.data ?? res;
  //       setCsvUploadEnabled(data?.csvUploadEnabled === true);
  //     })
  //     .catch(() => {
  //       if (cancelled) return;
  //       setCsvUploadEnabled(false);
  //     })
  //     .finally(() => {
  //       if (!cancelled) {
  //         setCsvUploadFeatureLoading(false);
  //       }
  //     });

  //   return () => {
  //     cancelled = true;
  //   };
  // }, [shopName]);

  const getCsvTooManyErrorsFailure = (data) => {
    if (!data || data.success !== false) return null;
    if (data.errorCode === "CSV_TOO_MANY_ERRORS") return data;
    return null;
  };

  const getCsvValidationFailure = (data) => {
    if (!data || data.success !== false) return null;
    if (data.errorCode === "CSV_TOO_MANY_ERRORS") return null;
    if (Array.isArray(data.errors) && data.errors.length > 0) return data;
    if (data.errorCode === "CSV_VALIDATION_FAILED") return data;
    return null;
  };

  const closeValidationModal = useCallback(() => {
    setValidationModalOpen(false);
    setValidationModalData(null);
  }, []);

  const closeTooManyErrorsModal = useCallback(() => {
    setTooManyErrorsModalOpen(false);
    setTooManyErrorsModalData(null);
  }, []);

  const applySettingsAfterUploadDismiss = useCallback(() => {
    if (typeof refreshSettings === "function") {
      refreshSettings();
    }
  }, [refreshSettings]);

  const closeUploadSuccessModal = useCallback(() => {
    setUploadSuccessModalOpen(false);
    setUploadSuccessMessage("");
    applySettingsAfterUploadDismiss();
  }, [applySettingsAfterUploadDismiss]);

  const closeUploadEmailModal = useCallback(() => {
    setUploadEmailModalOpen(false);
    applySettingsAfterUploadDismiss();
  }, [applySettingsAfterUploadDismiss]);

  const showToast = useCallback((message, isError = false) => {
    setToastMessage(message);
    setToastError(isError);
    setToastActive(true);
  }, []);

  const toggleToastActive = useCallback(() => {
    setToastActive((prev) => !prev);
  }, []);

  const loadCollections = useCallback(async () => {
    setCollectionsLoading(true);
    setCollectionsError("");
    try {
      const res = await productsApi.getShopProductUniques();
      const body = res?.data ?? res;
      const collections = body?.data?.uniqueCollections ?? [];
      setAvailableCollections(
        Array.isArray(collections)
          ? collections
              .filter((collection) => collection?.id && collection?.name)
              .map((collection) => ({
                label: collection.name,
                value: String(collection.id),
              }))
          : [],
      );
    } catch {
      setAvailableCollections([]);
      setCollectionsError("Unable to load collections. Please try again.");
    } finally {
      setCollectionsLoading(false);
    }
  }, []);

  const openDownloadOptionsModal = useCallback(() => {
    setDownloadOptionsModalOpen(true);
    setCollectionSearch("");
    if (availableCollections.length === 0 && !collectionsLoading) {
      loadCollections();
    }
  }, [availableCollections.length, collectionsLoading, loadCollections]);

  const closeDownloadOptionsModal = useCallback(() => {
    setDownloadOptionsModalOpen(false);
  }, []);

  const handleDownloadWithOptions = useCallback(() => {
    if (typeof downloadCSV !== "function") return;

    const params =
      downloadScope === "collection"
        ? { collectionIds: selectedCollectionIds.join(",") }
        : {};

    downloadCSV(params);
    closeDownloadOptionsModal();
  }, [
    closeDownloadOptionsModal,
    downloadCSV,
    downloadScope,
    selectedCollectionIds,
  ]);

  const validateAndSetFile = useCallback((next) => {
    setError("");
    if (!next) return;

    const isCsv =
      next.type === "text/csv" ||
      next.name.toLowerCase().endsWith(".csv") ||
      next.type === "application/vnd.ms-excel";

    if (!isCsv) {
      setError("Please upload a CSV file.");
      return;
    }

    if (typeof next.size === "number" && next.size > MAX_CSV_BYTES) {
      setError("CSV file must be 10MB or smaller.");
      return;
    }

    setFile(next);
  }, []);

  const uploadCsv = useCallback(async (nextFile) => {
    if (!nextFile || isUploading) return;
    setError("");
    setIsUploading(true);
    uploadSlowHandledRef.current = false;

    const slowTimer = window.setTimeout(() => {
      uploadSlowHandledRef.current = true;
      setIsUploading(false);
      setUploadEmailModalOpen(true);
    }, UPLOAD_SLOW_FALLBACK_MS);

    try {
      const res = await bulkActionApi.pricesImportCsv(nextFile);
      window.clearTimeout(slowTimer);

      const data = res?.data ?? res;
      const tooManyErrorsFailure = getCsvTooManyErrorsFailure(data);
      if (tooManyErrorsFailure) {
        if (uploadSlowHandledRef.current) return;
        setTooManyErrorsModalData(tooManyErrorsFailure);
        setTooManyErrorsModalOpen(true);
        return;
      }
      const validationFailure = getCsvValidationFailure(data);
      if (validationFailure) {
        if (uploadSlowHandledRef.current) return;
        setValidationModalData(validationFailure);
        setValidationModalOpen(true);
        return;
      }
      if (data?.success === false) {
        if (uploadSlowHandledRef.current) return;
        showToast(data?.message || "Upload failed.", true);
        return;
      }

      if (uploadSlowHandledRef.current) {
        return;
      }
      setUploadSuccessMessage(data?.message || "Upload in progress.");
      setUploadSuccessModalOpen(true);
    } catch (e) {
      window.clearTimeout(slowTimer);
      if (uploadSlowHandledRef.current) return;

      const errData =
        e?.error?.data ??
        e?.error?.error?.data ??
        e?.response?.data ??
        e?.data;
      const tooManyErrorsFailure = getCsvTooManyErrorsFailure(errData);
      if (tooManyErrorsFailure) {
        setTooManyErrorsModalData(tooManyErrorsFailure);
        setTooManyErrorsModalOpen(true);
        return;
      }
      const validationFailure = getCsvValidationFailure(errData);
      if (validationFailure) {
        setValidationModalData(validationFailure);
        setValidationModalOpen(true);
        return;
      }
      const msg =
        (typeof errData === "string" && errData) ||
        errData?.message ||
        e?.message ||
        "Upload failed. Please try again.";
      showToast(msg, true);
    } finally {
      window.clearTimeout(slowTimer);
      if (!uploadSlowHandledRef.current) {
        setIsUploading(false);
        setFile(null);
      }
    }
  }, [isUploading, showToast]);

  const isHighTraffic = csvUploadJobTotalCount > CSV_UPLOAD_MAX_QUEUE_COUNT;
  const isCatalogUpdateRunning = isCatalogPriceUpdateRunning(
    catalogPriceUpdateRunningMarkets,
  );
  const isUploadBlocked =
    // !csvUploadEnabled ||
    isCsvUploadJobRunning ||
    isCatalogUpdateRunning ||
    isHighTraffic;

  const isDownloadBlocked = isCsvDownloadRunning || bulkDownloadApiCalled;

  const getDownloadTooltip = () => {
    if (isCsvDownloadRunning) {
      return DOWNLOAD_TOOLTIP_EXPORT_IN_PROGRESS;
    }
    return null;
  };

  const getUploadTooltip = () => {
    if (csvUploadFeatureLoading) {
      return null;
    }
    // if (!csvUploadEnabled) {
    //   return UPLOAD_TOOLTIP_COMING_SOON;
    // }
    if (isCsvUploadJobRunning) {
      return UPLOAD_TOOLTIP_IN_PROGRESS;
    }
    if (isCatalogUpdateRunning) {
      return UPLOAD_TOOLTIP_CATALOG_UPDATE_IN_PROGRESS;
    }
    if (isHighTraffic) {
      return UPLOAD_TOOLTIP_HIGH_TRAFFIC;
    }
    return null;
  };

  const uploadTooltip = getUploadTooltip();
  const isUploadButtonDisabled =
    isUploading ||
    bulkDownloadApiCalled ||
    csvUploadFeatureLoading ||
    isUploadBlocked;

  const handleUploadClick = useCallback(() => {
    if (isUploading || bulkDownloadApiCalled || isUploadBlocked) return;
    if (fileInputRef.current) fileInputRef.current.click();
  }, [isUploading, bulkDownloadApiCalled, isUploadBlocked]);

  const toastMarkup =
    toastActive ? (
      <Toast
        content={toastMessage}
        error={toastError}
        onDismiss={toggleToastActive}
      />
    ) : null;

  const uploadSuccessModalMarkup = (
    <Modal
      open={uploadSuccessModalOpen}
      onClose={closeUploadSuccessModal}
      title="Upload in progress"
      primaryAction={{
        content: "OK",
        onAction: closeUploadSuccessModal,
      }}
    >
      <Modal.Section>
        <TextContainer>
          <p>{uploadSuccessMessage}</p>
        </TextContainer>
      </Modal.Section>
    </Modal>
  );

  const uploadEmailModalMarkup = (
    <Modal
      open={uploadEmailModalOpen}
      onClose={closeUploadEmailModal}
      title="CSV upload in progress"
      primaryAction={{
        content: "OK",
        onAction: closeUploadEmailModal,
      }}
    >
      <Modal.Section>
        <TextContainer>
          <p>
            Your upload is taking longer than expected. We will email you at
            your registered store email address once your price updates are
            processed.
          </p>
        </TextContainer>
      </Modal.Section>
    </Modal>
  );

  const validationModalMarkup = (
    <Modal
      open={validationModalOpen}
      onClose={closeValidationModal}
      title={validationModalData?.message || "CSV validation failed"}
      primaryAction={{
        content: "Close",
        onAction: closeValidationModal,
      }}
      large
    >
      <Modal.Section>
        <Stack vertical spacing="loose">
          {(validationModalData?.errors || []).map((item, index) => (
            <div key={`csv-error-${index}`}>
              <TextContainer>
                <p className="bold-font">
                  Row {item.rowNumber}
                  {item.column ? ` · ${item.column}` : ""}
                </p>
                <p>{item.message}</p>
              </TextContainer>
              {Array.isArray(item.stepsToResolve) && item.stepsToResolve.length > 0 ? (
                <div className="mt-10">
                  <List type="number">
                    {item.stepsToResolve.map((step, stepIndex) => (
                      <List.Item key={`csv-error-step-${index}-${stepIndex}`}>{step}</List.Item>
                    ))}
                  </List>
                </div>
              ) : null}
            </div>
          ))}
        </Stack>
      </Modal.Section>
    </Modal>
  );

  const tooManyErrorsModalMarkup = (
    <Modal
      open={tooManyErrorsModalOpen}
      onClose={closeTooManyErrorsModal}
      title="Too many invalid prices in CSV"
      secondaryActions={[
        {
          content: "Close",
          onAction: closeTooManyErrorsModal,
        },
      ]}
    >
      <Modal.Section>
        <Stack vertical spacing="loose">
          <Banner status="critical">
            <p>
              {tooManyErrorsModalData?.message ||
                "Too many invalid prices were found in this CSV. Please download the CSV file again, update the prices, and upload it again."}
            </p>
          </Banner>


          <TextContainer>
            <p className="bold-font">Steps to resolve</p>
            <List type="number">
              <List.Item>Download the CSV file again.</List.Item>
              <List.Item>
                Fix only the <span className="bold-font">price columns</span>, then upload
                the corrected file again. Do not leave prices empty or set them to null.
              </List.Item>
            </List>
          </TextContainer>
        </Stack>
      </Modal.Section>
    </Modal>
  );

  const uploadCsvButton = (
    <Button
      loading={isUploading || csvUploadFeatureLoading}
      disabled={isUploadButtonDisabled}
      onClick={handleUploadClick}
    >
      Upload CSV
    </Button>
  );

  const uploadCsvButtonMarkup = uploadTooltip ? (
    <Tooltip content={uploadTooltip} dismissOnMouseOut>
      <span className="uploadCsvButtonWrap">{uploadCsvButton}</span>
    </Tooltip>
  ) : (
    uploadCsvButton
  );

  const downloadCsvButton = (
    <Button
      disabled={typeof downloadCSV !== "function" || isDownloadBlocked}
      loading={bulkDownloadApiCalled}
      onClick={() => {
        if (typeof downloadCSV === "function" && !isDownloadBlocked) {
          openDownloadOptionsModal();
        }
      }}
    >
      Download CSV
    </Button>
  );

  const downloadTooltip = getDownloadTooltip();
  const downloadCsvButtonMarkup = downloadTooltip ? (
    <Tooltip content={downloadTooltip} dismissOnMouseOut>
      <span className="uploadCsvButtonWrap">{downloadCsvButton}</span>
    </Tooltip>
  ) : (
    downloadCsvButton
  );

  const selectedCollectionLabels = availableCollections
    .filter((collection) => selectedCollectionIds.includes(collection.value))
    .map((collection) => collection.label);
  const filteredCollections = availableCollections.filter((collection) =>
    collection.label.toLowerCase().includes(collectionSearch.toLowerCase()),
  );

  const downloadOptionsModalMarkup = (
    <Modal
      open={downloadOptionsModalOpen}
      onClose={closeDownloadOptionsModal}
      title="Download pricing CSV"
      primaryAction={{
        content: "Download CSV",
        onAction: handleDownloadWithOptions,
        disabled:
          bulkDownloadApiCalled ||
          (downloadScope === "collection" && selectedCollectionIds.length === 0),
        loading: bulkDownloadApiCalled,
      }}
      secondaryActions={[
        {
          content: "Cancel",
          onAction: closeDownloadOptionsModal,
        },
      ]}
    >
      <Modal.Section>
        <Stack vertical spacing="loose">
          <TextContainer>
            <p>Choose which products to include in your pricing CSV.</p>
          </TextContainer>

          <Select
            label="Products to download"
            options={[
              { label: "All products", value: "all" },
              { label: "By collection", value: "collection" },
            ]}
            value={downloadScope}
            onChange={setDownloadScope}
          />

          {downloadScope === "collection" ? (
            <>
              {collectionsError ? (
                <Banner
                  status="critical"
                  action={{ content: "Retry", onAction: loadCollections }}
                >
                  <p>{collectionsError}</p>
                </Banner>
              ) : null}

              {selectedCollectionLabels.length > 0 ? (
                <Banner status="info">
                  <p>
                    <span className="bold-font">
                      {selectedCollectionLabels.length} collection
                      {selectedCollectionLabels.length === 1 ? "" : "s"} selected:
                    </span>{" "}
                    {selectedCollectionLabels.join(", ")}
                  </p>
                </Banner>
              ) : null}

              <div style={{ marginTop: 20 }}>
                <TextField
                  label="Search collections"
                  value={collectionSearch}
                  onChange={setCollectionSearch}
                  placeholder="Search by collection name"
                  clearButton
                  onClearButtonClick={() => setCollectionSearch("")}
                  disabled={collectionsLoading}
                />
              </div>

              {collectionsLoading ? (
                <TextContainer>
                  <p>Loading collections…</p>
                </TextContainer>
              ) : null}

              {!collectionsLoading &&
              !collectionsError &&
              availableCollections.length === 0 ? (
                <Banner status="info">
                  <p>No collections are available for this store.</p>
                </Banner>
              ) : null}

              {!collectionsLoading &&
              availableCollections.length > 0 &&
              filteredCollections.length === 0 ? (
                <Banner status="info">
                  <p>No collections match “{collectionSearch}”.</p>
                </Banner>
              ) : null}

              {filteredCollections.length > 0 ? (
                <div style={{ marginTop: 16 }}>
                  <div
                    style={{
                      maxHeight: 260,
                      overflowY: "auto",
                      border: "1px solid #dfe3e8",
                      borderRadius: 4,
                      padding: "0 12px",
                    }}
                  >
                    <ChoiceList
                      title="Select collections"
                      titleHidden={false}
                      allowMultiple
                      choices={filteredCollections}
                      selected={selectedCollectionIds}
                      onChange={setSelectedCollectionIds}
                      disabled={collectionsLoading}
                    />
                  </div>
                </div>
              ) : null}
            </>
          ) : (
            <Banner status="info">
              <p>The CSV will include all products in your store.</p>
            </Banner>
          )}
        </Stack>
      </Modal.Section>
    </Modal>
  );

  const cardMarkup = (
    <Card sectioned>
      <div className="bulkDownload">
        {error ? (
          <>
            <Banner status="critical" title="Upload failed">
              <p>{error}</p>
            </Banner>
            <div className="mt-20"></div>
          </>
        ) : null}

        <List type="number">
          <List.Item>
            Click the “<span className="bold-font">Download CSV</span>” button below to download your pricing file.
          </List.Item>
          <List.Item>
            Update only the <span className="bold-font">price columns</span> for the products you want to change.
          </List.Item>
          <List.Item>
            Upload CSV file using the “<span className="bold-font">Upload CSV</span>” button.
          </List.Item>
        </List>

        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          style={{ display: "none" }}
          onChange={(e) => {
            if (isUploadBlocked) {
              e.target.value = "";
              return;
            }
            const next = e.target.files && e.target.files[0];
            validateAndSetFile(next);
            if (
              next &&
              (next.type === "text/csv" ||
                next.name.toLowerCase().endsWith(".csv") ||
                next.type === "application/vnd.ms-excel") &&
              !(typeof next.size === "number" && next.size > MAX_CSV_BYTES)
            ) {
              setFile(next);
              uploadCsv(next);
            }
            e.target.value = "";
          }}
        />

        <div className="mt-20"></div>
        <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
          <div>{downloadCsvButtonMarkup}</div>
          <div>{uploadCsvButtonMarkup}</div>
        </div>

        <div className="mt-20"></div>
        <Banner status="info">
          <div>
            <div className="bold-font" style={{ marginBottom: 4 }}>
              Important Note:
            </div>
            <div style={{ marginTop: 0 }}>
              <List type="bullet">
                <List.Item>
                  Only <span className="bold-font">CSV files</span> are supported (maximum size: 10MB).
                </List.Item>
                <List.Item>
                  <span className="bold-font">Do not modify any fields other than product prices</span>, as this may cause errors during upload.
                </List.Item>
              </List>
            </div>
          </div>
        </Banner>

        <p className="mt-15">
          Do let us know at{" "}
          <a
            rel="noreferrer noopener"
            aria-label="support@mlveda.com (opens in a new tab)"
            href="mailto:support@mlveda.com"
            target="_blank"
          >
            <span className="emailLink">support@mlveda.com</span>
          </a>{" "}
          if you face any issues while uploading the CSV file.
        </p>
      </div>
    </Card>
  );

  if (embedded) {
    return (
      <>
        {cardMarkup}
        {toastMarkup}
        {validationModalMarkup}
        {tooManyErrorsModalMarkup}
        {uploadSuccessModalMarkup}
        {uploadEmailModalMarkup}
        {downloadOptionsModalMarkup}
      </>
    );
  }

  const annotatedTitle = sectionTitle || "Upload CSV";
  const annotatedDescription =
    sectionDescription || "Upload a CSV file to update product prices in bulk";

  return (
    <Layout>
      <Layout.AnnotatedSection
        title={annotatedTitle}
        description={annotatedDescription}
      >
        {cardMarkup}
      </Layout.AnnotatedSection>
      {toastMarkup}
      {validationModalMarkup}
      {tooManyErrorsModalMarkup}
      {uploadSuccessModalMarkup}
      {uploadEmailModalMarkup}
      {downloadOptionsModalMarkup}
    </Layout>
  );
};

const mapDispatchToProps = {
  refreshSettings,
};

export default connect(null, mapDispatchToProps)(UploadCsv);